import {
  ChannelIdSchema,
  PARTY_RECENTLY_CREATED_WINDOW_MS,
  PartySlugSchema,
  isReservedPartySlug,
  type ActiveParty,
  type Channel,
  type ChannelId,
  type PartySlug,
  type PastParty,
  type PublicChannel,
} from '@mixwire/contracts';
import { useState, type ReactElement } from 'react';

import { loginUrl } from '../api/auth.js';
import { readApiFailure } from '../api/client.js';
import { EndPartyConfirmModal } from '../components/EndPartyConfirmModal.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { IdentityChip } from '../components/IdentityChip.js';
import { InfoTooltip } from '../components/InfoTooltip.js';
import { LoadingStatus } from '../components/LoadingStatus.js';
import { Logo } from '../components/Logo.js';
import { pillButton } from '../components/pillButton.js';
import { useActiveParties } from '../hooks/useActiveParties.js';
import { useAuthStatus } from '../hooks/useAuth.js';
import { useCreateParty } from '../hooks/useCreateParty.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useEndParty } from '../hooks/useEndParty.js';
import { useJoinChannel } from '../hooks/useJoinChannel.js';
import { usePastParties } from '../hooks/usePastParties.js';
import { useResolveParty } from '../hooks/useResolveParty.js';
import { useTakeoverChannel } from '../hooks/useTakeoverChannel.js';
import { colourName } from '../lib/channelColour.js';
import { partySlugOf } from '../lib/partySlug.js';
import {
  JOIN_QUERY_PARAM,
  LOBBY_PATH,
  displayLink,
  djPath,
  exportPath,
  partyPath,
  wrappedPath,
} from '../routing/paths.js';
import { useRoute } from '../routing/useRoute.js';

/** Someone sent here to sign in from a party page arrives with that party already chosen. */
const readJoinRequest = (): string | undefined =>
  new URLSearchParams(location.search).get(JOIN_QUERY_PARAM) ?? undefined;

const CHANNEL_IDS: readonly ChannelId[] = [
  ChannelIdSchema.parse(1),
  ChannelIdSchema.parse(2),
  ChannelIdSchema.parse(3),
];

const ChannelRow = ({
  slot,
  onPick,
}: {
  readonly slot: PublicChannel;
  readonly onPick: (slot: PublicChannel) => void;
}): ReactElement => (
  <div className="border-subtle flex items-center gap-3 border-b px-5 py-4 last:border-b-0">
    <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: slot.colour }} />
    <div className="min-w-0 flex-1">
      {slot.channel === null ? (
        <>
          <p className="text-content text-sm font-semibold">{colourName(slot.colour)} · free</p>
          <p className="text-muted text-xs">No DJ on this channel</p>
        </>
      ) : (
        <>
          <p className="text-content text-sm font-semibold">
            {slot.channel.displayName} · {colourName(slot.colour)}
          </p>
          <p className="text-muted text-xs">active</p>
        </>
      )}
    </div>
    <button
      type="button"
      onClick={() => {
        onPick(slot);
      }}
      className={pillButton('quiet-strong', 'sm')}
    >
      {slot.channel === null ? 'Available' : 'Take over'}
    </button>
  </div>
);

// A just-created party is almost certainly about to be staffed by its
// creator, so it's labelled "Just started" rather than shown as empty.
const isRecentlyCreated = (party: ActiveParty): boolean =>
  Date.now() - new Date(party.createdAt).getTime() < PARTY_RECENTLY_CREATED_WINDOW_MS;

// Live is decided by the server's expiresAt, not re-derived from play
// history, so a room whose only DJ just stepped off still counts as live.
const isLive = (party: ActiveParty): boolean =>
  party.djCount > 0 || Date.now() < new Date(party.expiresAt).getTime();

// An empty party still inside its live window reads as "Decks free", not a
// headcount — that's the invitation to pick it up.
const djLabel = (party: ActiveParty, isQuiet: boolean): string => {
  if (party.djCount > 0) {
    return `${String(party.djCount)} of ${String(CHANNEL_IDS.length)} DJs`;
  }
  if (isRecentlyCreated(party)) {
    return 'Just started';
  }
  return isQuiet ? `0 of ${String(CHANNEL_IDS.length)} DJs` : 'Decks free';
};

const PartyRow = ({
  party,
  isQuiet,
  onSelect,
}: {
  readonly party: ActiveParty;
  readonly isQuiet: boolean;
  readonly onSelect: (party: ActiveParty) => void;
}): ReactElement => (
  <button
    type="button"
    onClick={() => {
      onSelect(party);
    }}
    className={`border-subtle hover:bg-surface-hover flex w-full items-center justify-between border-b px-5 py-4 text-left last:border-b-0 ${isQuiet ? 'opacity-60' : ''}`}
  >
    <span
      className={
        isQuiet ? 'text-muted text-sm font-semibold' : 'text-content text-sm font-semibold'
      }
    >
      {party.displayName}
    </span>
    <span className="text-muted text-xs">{djLabel(party, isQuiet)}</span>
  </button>
);

const ActivePartyList = ({
  parties,
  onSelect,
  onNewParty,
}: {
  readonly parties: readonly ActiveParty[];
  readonly onSelect: (party: ActiveParty) => void;
  readonly onNewParty: () => void;
}): ReactElement => {
  // The server already orders parties newest-activity-first; the client only
  // decides the live/quiet boundary.
  const live = parties.filter(isLive);
  const quiet = parties.filter((party) => !isLive(party));

  return (
    <div className="bg-surface rounded-card border-subtle border">
      <div className="border-subtle flex items-center justify-between border-b px-5 py-4">
        <p className="text-secondary text-sm font-medium">Live now</p>
        <button type="button" onClick={onNewParty} className={pillButton('quiet-strong', 'sm')}>
          + New party
        </button>
      </div>
      {parties.length === 0 ? (
        <p className="text-muted p-6 text-center text-sm">No parties are live right now.</p>
      ) : (
        <>
          {live.map((party) => (
            <PartyRow key={party.slug} party={party} isQuiet={false} onSelect={onSelect} />
          ))}
          {quiet.length === 0 ? null : (
            <p className="text-muted border-subtle border-b px-5 py-2 text-xs tracking-widest uppercase">
              No DJs yet
            </p>
          )}
          {quiet.map((party) => (
            <PartyRow key={party.slug} party={party} isQuiet={true} onSelect={onSelect} />
          ))}
        </>
      )}
    </div>
  );
};

type NewPartyFormProps = {
  readonly partyName: string;
  readonly onPartyNameChange: (value: string) => void;
  readonly isPending: boolean;
  readonly onCreate: () => void;
  readonly onCancel: () => void;
};

type NameVerdict =
  | { readonly kind: 'blank' }
  | { readonly kind: 'reserved' }
  | { readonly kind: 'too-long' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'taken' }
  | { readonly kind: 'free'; readonly slug: PartySlug };

const NAME_CHECK_DELAY_MS = 300;

const useNameVerdict = (partyName: string): NameVerdict => {
  const slug = partySlugOf(partyName);
  const settledSlug = useDebouncedValue(slug, NAME_CHECK_DELAY_MS);
  const pastParties = usePastParties();
  const resolve = useResolveParty(settledSlug);

  if (slug === '') {
    return { kind: 'blank' };
  }
  if (isReservedPartySlug(slug)) {
    return { kind: 'reserved' };
  }

  const parsedSlug = PartySlugSchema.safeParse(slug);
  if (!parsedSlug.success) {
    return { kind: 'too-long' };
  }
  if (pastParties.data?.some((past) => past.slug === slug) === true) {
    return { kind: 'taken' };
  }
  if (settledSlug !== slug || resolve.isPending) {
    return { kind: 'checking' };
  }
  const found = resolve.data?.party ?? null;
  return found === null ? { kind: 'free', slug: parsedSlug.data } : { kind: 'taken' };
};

const RestrictedNameNote = (): ReactElement => (
  <span className="text-warning flex items-center gap-1.5 text-xs font-normal">
    Restricted — this name is not available.
    <InfoTooltip label="Why is this name restricted?">
      Party links are as short as {location.host}/party-name. The price of that is a handful of
      names that would collide with MixWire’s own endpoints, so those names are unavailable.
    </InfoTooltip>
  </span>
);

const NameVerdictNote = ({ verdict }: { readonly verdict: NameVerdict }): ReactElement | null => {
  switch (verdict.kind) {
    case 'blank': {
      return null;
    }
    case 'reserved': {
      return <RestrictedNameNote />;
    }
    case 'too-long': {
      return (
        <span className="text-danger text-xs font-normal">That name is too long for a link.</span>
      );
    }
    case 'checking': {
      return <span className="text-muted text-xs font-normal">Checking the name…</span>;
    }
    case 'taken': {
      return (
        <span className="text-danger text-xs font-normal">Taken — this party already exists.</span>
      );
    }
    case 'free': {
      return (
        <span className="text-muted truncate text-xs font-normal">
          Your link:{' '}
          <span className="text-content font-medium">{displayLink(partyPath(verdict.slug))}</span>
        </span>
      );
    }
  }
};

const PAST_PARTY_DATE = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' });

const PastPartyLink = ({
  href,
  label,
  children,
  onNavigate,
}: {
  readonly href: string;
  readonly label: string;
  readonly children: string;
  readonly onNavigate: (path: string) => void;
}): ReactElement => (
  <a
    href={href}
    aria-label={label}
    onClick={(event) => {
      event.preventDefault();
      onNavigate(href);
    }}
    className={pillButton('quiet-strong', 'sm')}
  >
    {children}
  </a>
);

const PastPartyRow = ({
  past,
  variant,
  onNavigate,
}: {
  readonly past: PastParty;
  readonly variant: 'card' | 'compact';
  readonly onNavigate: (path: string) => void;
}): ReactElement => (
  <li
    className={
      variant === 'card'
        ? 'border-subtle flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-5 py-3.5 last:border-b-0'
        : 'flex flex-wrap items-center justify-between gap-x-4 gap-y-1'
    }
  >
    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
      <span
        className={
          variant === 'card'
            ? 'text-content min-w-0 truncate text-sm font-semibold'
            : 'text-muted min-w-0 truncate text-xs'
        }
      >
        {past.displayName}
      </span>
      <span className="text-muted shrink-0 text-xs tabular-nums">
        {PAST_PARTY_DATE.format(new Date(past.createdAt))}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <PastPartyLink
        href={wrappedPath(past.slug)}
        label={`Wrapped for ${past.displayName}`}
        onNavigate={onNavigate}
      >
        Wrapped
      </PastPartyLink>
      <PastPartyLink
        href={exportPath(past.slug)}
        label={`Export for ${past.displayName}`}
        onNavigate={onNavigate}
      >
        Export
      </PastPartyLink>
    </span>
  </li>
);

const usePastPartyRows = (): {
  readonly parties: readonly PastParty[];
  readonly navigate: (path: string) => void;
} => {
  const pastParties = usePastParties();
  const { navigate } = useRoute();

  return { parties: pastParties.data ?? [], navigate };
};

/** The name-collision helper inside the new-party form, not a party history. */
const PastPartyList = (): ReactElement | null => {
  const { parties, navigate } = usePastPartyRows();

  if (parties.length === 0) {
    return null;
  }

  return (
    <div className="border-subtle border-t pt-4">
      <p className="text-muted text-xs tracking-widest uppercase">Names already used</p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {parties.map((past) => (
          <PastPartyRow key={past.slug} past={past} variant="compact" onNavigate={navigate} />
        ))}
      </ul>
    </div>
  );
};

const PastPartyCard = (): ReactElement | null => {
  const { parties, navigate } = usePastPartyRows();

  if (parties.length === 0) {
    return null;
  }

  return (
    <div className="bg-surface rounded-card border-subtle border">
      <div className="border-subtle border-b px-5 py-4">
        <p className="text-secondary text-sm font-medium">Past parties</p>
      </div>
      <ul className="flex flex-col">
        {parties.map((past) => (
          <PastPartyRow key={past.slug} past={past} variant="card" onNavigate={navigate} />
        ))}
      </ul>
    </div>
  );
};

const NewPartyForm = ({
  partyName,
  onPartyNameChange,
  isPending,
  onCreate,
  onCancel,
}: NewPartyFormProps): ReactElement => {
  const verdict = useNameVerdict(partyName);
  const canSubmit = !isPending && verdict.kind === 'free';

  return (
    <div className="bg-surface rounded-card border-subtle border p-5">
      <p className="text-muted text-xs tracking-widest uppercase">Start a new party</p>
      <div className="mt-4 flex flex-col gap-3">
        <label className="text-secondary block text-sm font-medium">
          <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            Party name
            <NameVerdictNote verdict={verdict} />
          </span>
          <input
            value={partyName}
            onChange={(event) => {
              onPartyNameChange(event.target.value);
            }}
            placeholder="Softwire summer social"
            className="border-line text-content placeholder:text-muted mt-1.5 w-full rounded-control border px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onCreate}
          className={`${pillButton('solid')} mt-1`}
        >
          Go
        </button>
        <button type="button" onClick={onCancel} className={pillButton('ghost')}>
          Cancel
        </button>
      </div>

      <PastPartyList />
    </div>
  );
};

type PickerState =
  | { readonly kind: 'join'; readonly channelId: ChannelId; readonly colour: string }
  | {
      readonly kind: 'takeover';
      readonly channelId: ChannelId;
      readonly colour: string;
      readonly occupant: Channel;
    };

type CredentialFieldProps = {
  readonly displayName: string;
  readonly onDisplayNameChange: (value: string) => void;
};

const CredentialField = ({
  displayName,
  onDisplayNameChange,
}: CredentialFieldProps): ReactElement => (
  <label className="text-secondary text-sm font-medium">
    Channel display name <span className="text-muted">(e.g. your first name)</span>
    <input
      value={displayName}
      onChange={(event) => {
        onDisplayNameChange(event.target.value);
      }}
      className="border-line text-content mt-1.5 w-full rounded-control border px-3 py-2 text-sm"
    />
  </label>
);

type RightPanelProps = {
  readonly picker: PickerState | undefined;
  readonly onCancelPicker: () => void;
  readonly displayName: string;
  readonly onDisplayNameChange: (value: string) => void;
  readonly isPending: boolean;
  readonly onJoin: (channelId: ChannelId) => void;
  readonly onTakeover: (channelId: ChannelId) => void;
};

const RightPanel = ({
  picker,
  onCancelPicker,
  displayName,
  onDisplayNameChange,
  isPending,
  onJoin,
  onTakeover,
}: RightPanelProps): ReactElement => {
  if (picker === undefined) {
    return <p className="text-muted text-sm">Pick a channel on the left to join or take over.</p>;
  }

  const canSubmit = !isPending && displayName.trim() !== '';

  if (picker.kind === 'join') {
    return (
      <>
        <p className="text-muted text-xs tracking-widest uppercase">Take a channel</p>
        <h2 className="font-display text-content mt-1 text-xl">
          The {colourName(picker.colour).toLowerCase()} channel
        </h2>
        <div className="mt-4 flex flex-col gap-3">
          <CredentialField displayName={displayName} onDisplayNameChange={onDisplayNameChange} />
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onJoin(picker.channelId);
            }}
            className={`${pillButton('accent')} mt-1`}
          >
            Take the {colourName(picker.colour).toLowerCase()} channel
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="text-muted text-xs tracking-widest uppercase">Replace a DJ</p>
      <h2 className="font-display text-content mt-1 text-xl">
        Replace {picker.occupant.displayName}?
      </h2>
      <div
        className="bg-canvas mt-3 rounded-control border-l-4 px-3.5 py-2.5"
        style={{ borderLeftColor: picker.colour }}
      >
        <p className="text-content text-sm font-medium">
          The {colourName(picker.colour).toLowerCase()} channel
        </p>
      </div>
      <p className="text-secondary mt-3 text-xs leading-relaxed">
        They are dropped from the party straight away and lose the channel. What they played tonight
        stays in the party history, and the crowd-wants list is untouched.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <CredentialField displayName={displayName} onDisplayNameChange={onDisplayNameChange} />
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            onTakeover(picker.channelId);
          }}
          className={`${pillButton('solid')} mt-1`}
        >
          Yes, take the {colourName(picker.colour).toLowerCase()} channel
        </button>
        <button type="button" onClick={onCancelPicker} className={pillButton('ghost')}>
          Cancel
        </button>
      </div>
    </>
  );
};

const SignInGate = (): ReactElement => (
  <div className="bg-surface rounded-card border-subtle mx-auto flex max-w-sm flex-col items-center gap-4 border p-8 text-center">
    <Logo className="text-content size-16" />
    <p className="text-muted text-xs tracking-widest uppercase">DJ login</p>
    <h1 className="font-display text-content text-2xl">Sign in to join MixWire</h1>
    <p className="text-secondary text-sm">
      Log in with Spotify to see who’s live right now and join or start a party.
    </p>
    <a href={loginUrl} className={`${pillButton('accent')} mt-2`}>
      Log in with Spotify
    </a>
  </div>
);

export const JoinPage = (): ReactElement | null => {
  const [isCreating, setIsCreating] = useState(false);
  const [newPartyName, setNewPartyName] = useState('');
  const [checkedName, setCheckedName] = useState<string | undefined>(readJoinRequest);
  const [displayName, setDisplayName] = useState('');
  const [picker, setPicker] = useState<PickerState | undefined>(undefined);
  const [isEndingParty, setIsEndingParty] = useState(false);

  const authStatus = useAuthStatus();
  const isAuthenticated = authStatus.data?.authenticated === true;
  const activeParties = useActiveParties(isAuthenticated);
  const resolve = useResolveParty(checkedName ?? '');
  const startPartyMutation = useCreateParty();
  const joinChannel = useJoinChannel();
  const takeoverChannel = useTakeoverChannel();
  const endParty = useEndParty();
  const { navigate } = useRoute();

  if (authStatus.data === undefined) {
    return null;
  }

  if (!isAuthenticated) {
    return <SignInGate />;
  }

  const isPending =
    startPartyMutation.isPending || joinChannel.isPending || takeoverChannel.isPending;
  const failure =
    readApiFailure(startPartyMutation.error) ??
    readApiFailure(joinChannel.error) ??
    readApiFailure(takeoverChannel.error);

  const resolvedParty = resolve.data?.party ?? null;

  const onBackToAllParties = (): void => {
    setCheckedName(undefined);
    setPicker(undefined);
    setIsEndingParty(false);
    if (location.search !== '') {
      navigate(LOBBY_PATH);
    }
  };

  const goToConsole = (partySlug: PartySlug, channelId: ChannelId): void => {
    navigate(djPath(partySlug, channelId));
  };

  const submitCreate = (): void => {
    if (newPartyName.trim() === '') {
      return;
    }
    startPartyMutation.mutate(
      { partyName: newPartyName },
      {
        onSuccess: (response) => {
          // No channel is claimed yet — send the caller into the same join
          // flow anyone else uses, on the party they just made.
          setIsCreating(false);
          setNewPartyName('');
          setCheckedName(response.displayName);
          setPicker(undefined);
        },
      },
    );
  };

  const submitJoin = (channelId: ChannelId): void => {
    if (resolve.data?.party === undefined || resolve.data.party === null) {
      return;
    }
    joinChannel.mutate(
      { slug: resolve.data.party.slug, channelId, displayName },
      {
        onSuccess: (response) => {
          goToConsole(response.partySlug, channelId);
        },
      },
    );
  };

  const submitTakeover = (channelId: ChannelId): void => {
    if (resolve.data?.party === undefined || resolve.data.party === null) {
      return;
    }
    takeoverChannel.mutate(
      { slug: resolve.data.party.slug, channelId, displayName },
      {
        onSuccess: (response) => {
          goToConsole(response.partySlug, channelId);
        },
      },
    );
  };

  const newPartyPanel = (
    <NewPartyForm
      partyName={newPartyName}
      onPartyNameChange={setNewPartyName}
      isPending={isPending}
      onCreate={submitCreate}
      onCancel={() => {
        setIsCreating(false);
        setNewPartyName('');
      }}
    />
  );
  const browsePanel =
    checkedName === undefined ? (
      <>
        <ActivePartyList
          parties={activeParties.data ?? []}
          onSelect={(party) => {
            setCheckedName(party.displayName);
            setPicker(undefined);
          }}
          onNewParty={() => {
            setIsCreating(true);
          }}
        />
        <PastPartyCard />
      </>
    ) : null;
  const lobbyPanel = isCreating ? newPartyPanel : browsePanel;

  return (
    <div className="mx-auto flex w-full max-w-feed flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <Logo className="text-content size-10 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <p className="text-muted text-xs tracking-widest uppercase">DJ login</p>
              <h1 className="font-display text-content text-3xl leading-none">
                Join a party, or start one
              </h1>
            </div>
          </div>
          <IdentityChip />
        </div>
        <p className="text-secondary mt-4 text-sm">
          You can only be in one party at a time. MixWire reads what is playing — it never takes
          over your decks.
        </p>
      </div>

      {lobbyPanel}

      {checkedName !== undefined && resolve.isPending ? (
        <LoadingStatus className="">Checking…</LoadingStatus>
      ) : null}

      {checkedName === undefined || resolve.data === undefined ? null : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={onBackToAllParties}
              className={pillButton('quiet', 'sm')}
            >
              ← All parties
            </button>
            {resolve.data.party === null ? null : (
              <button
                type="button"
                onClick={() => {
                  setIsEndingParty(true);
                }}
                className={pillButton('quiet-danger', 'sm')}
              >
                End party
              </button>
            )}
          </div>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="bg-surface rounded-card border-subtle flex-1 overflow-hidden border">
              {resolve.data.party === null ? (
                <p className="text-muted p-6 text-center text-sm">
                  “{checkedName}” isn’t live any more.
                </p>
              ) : (
                resolve.data.party.channels.map((slot) => (
                  <ChannelRow
                    key={slot.id}
                    slot={slot}
                    onPick={(picked) => {
                      setPicker(
                        picked.channel === null
                          ? { kind: 'join', channelId: picked.id, colour: picked.colour }
                          : {
                              kind: 'takeover',
                              channelId: picked.id,
                              colour: picked.colour,
                              occupant: picked.channel,
                            },
                      );
                    }}
                  />
                ))
              )}
            </div>

            <div className="bg-surface rounded-card border-subtle w-full flex-1 border p-5">
              <RightPanel
                picker={picker}
                onCancelPicker={() => {
                  setPicker(undefined);
                }}
                displayName={displayName}
                onDisplayNameChange={setDisplayName}
                isPending={isPending}
                onJoin={submitJoin}
                onTakeover={submitTakeover}
              />
            </div>
          </div>
        </>
      )}

      {isEndingParty && resolvedParty !== null ? (
        <EndPartyConfirmModal
          partyDisplayName={resolvedParty.displayName}
          isPending={endParty.isPending}
          onConfirm={() => {
            endParty.mutate(resolvedParty.slug, {
              onSuccess: onBackToAllParties,
            });
          }}
          onCancel={() => {
            setIsEndingParty(false);
          }}
        />
      ) : null}

      {failure === undefined ? null : (
        <ErrorBanner className="rounded-control px-3 py-2">{failure.message}</ErrorBanner>
      )}
    </div>
  );
};
